/**
 * progress-flow 의 정적 FlowDocument 빌더 모음.
 *
 * 기존에는 `MovingDateStepAdapter`, `PackingStepAdapter`, `CustomerRequestsStepAdapter`
 * 의 `useMemo` 안에 인라인으로 FlowDocument 가 정의되어 있었다. Firestore
 * `playground_flows` 컬렉션에 같은 문서를 등록하기 위해 빌더 함수로 추출했다.
 *
 * 각 빌더는:
 *   1. 어댑터에서 `useMemo(() => buildXxxFlow(), [])` 로 임포트되어 InquiryFlow 의 `flow`
 *      prop 으로 전달된다 (FlowDocument 가 Firestore 에 등록되기 전 fallback 경로).
 *   2. `scripts/register-flows.ts` 가 같은 빌더를 호출해 Firestore 에 직렬화한다.
 *
 * 따라서 어댑터 측 inline 정의를 변경할 때는 본 빌더만 수정하면 자동으로 Firestore
 * 측 등록 스크립트도 동일한 결과를 갖게 된다.
 *
 * `buildMovingMethodFlow` / `buildArrivalAddressFlow` 는 이미 별도 파일로 존재하므로
 * 여기서는 단순 인라인 FlowDocument 만 추출한다.
 */

import type { FlowDocument } from '@miso/design-system';
import type { PackingMethod } from '@/entities/visit-counsel/model/intake.types';

// ── moving-date ──────────────────────────────────────────────────────────────

export const MOVING_DATE_QUESTION_ID = 'movingDate';
const MOVING_DATE_STEP_ID = 'pick-date';
const MOVING_DATE_TERMINAL_ID = 'saved';

/**
 * 이사일자 FlowDocument — 단일 date input 한 개.
 * 어댑터: `MovingDateStepAdapter`.
 */
export function buildMovingDateFlow(): FlowDocument {
  return {
    schemaVersion: 1,
    id: 'progress-moving-date',
    initial: MOVING_DATE_STEP_ID,
    meta: { appBarTitle: '이사일자' },
    steps: {
      [MOVING_DATE_STEP_ID]: {
        kind: 'questions',
        questions: [
          {
            id: MOVING_DATE_QUESTION_ID,
            layout: 'L1',
            title: '언제 이사하시나요?',
            ctaLabel: '다음',
            input: {
              kind: 'date',
              required: true,
              placeholder: '이사일자',
              sheetTitle: '이사일자 선택',
              sheetCtaLabel: '확인',
            },
          },
        ],
        next: { kind: 'terminal', target: MOVING_DATE_TERMINAL_ID },
      },
    },
    terminals: { [MOVING_DATE_TERMINAL_ID]: { kind: 'placeholder-auto' } },
  };
}

// ── packing ──────────────────────────────────────────────────────────────────

export const PACKING_QUESTION_ID = 'packingMethod';
const PACKING_STEP_ID = 'pick';
const PACKING_TERMINAL_ID = 'saved';

const PACKING_OPTIONS: ReadonlyArray<{ value: PackingMethod; label: string }> = [
  { value: 'FULL_PACKING', label: '포장 이사' },
  { value: 'PARTIAL_PACKING', label: '반포장 이사' },
  { value: 'NO_PACKING', label: '일반 이사' },
];

/**
 * 포장방식 FlowDocument — 단일 single-select (포장/반포장/일반).
 * 어댑터: `PackingStepAdapter`.
 */
export function buildPackingFlow(): FlowDocument {
  return {
    schemaVersion: 1,
    id: 'progress-packing',
    initial: PACKING_STEP_ID,
    meta: { appBarTitle: '포장방식' },
    steps: {
      [PACKING_STEP_ID]: {
        kind: 'questions',
        questions: [
          {
            id: PACKING_QUESTION_ID,
            layout: 'L1',
            title: '포장방식을 알려주세요',
            ctaLabel: '다음',
            input: {
              kind: 'single-select',
              required: true,
              options: PACKING_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
            },
          },
        ],
        next: { kind: 'terminal', target: PACKING_TERMINAL_ID },
      },
    },
    terminals: { [PACKING_TERMINAL_ID]: { kind: 'placeholder-auto' } },
  };
}

// ── customer-requests ────────────────────────────────────────────────────────

export const CUSTOMER_REQUESTS_QUESTION_ID = 'questions';
export const CUSTOMER_REQUESTS_PAID_QUESTION_ID = 'paidQuoteRequests';
const CR_STEP_QUESTIONS = 'questions';
const CR_STEP_PAID = 'paid';
const CR_TERMINAL = 'saved';

/** "직접 입력" 트리거 sentinel — multi-select extensible 의 BottomSheet 를 연다. */
export const CUSTOMER_REQUESTS_CUSTOM_TRIGGER = '__custom__';

const CR_QUESTION_OPTIONS = [
  { value: '작업중 금연 부탁드려요', label: '작업중 금연 부탁드려요' },
  { value: '내국인 작업자 배정해주세요', label: '내국인 작업자 배정해주세요' },
  { value: CUSTOMER_REQUESTS_CUSTOM_TRIGGER, label: '직접 입력' },
];

const CR_PAID_OPTIONS = [
  { value: '반포장 이사 비교견적', label: '반포장 이사 비교견적' },
  { value: CUSTOMER_REQUESTS_CUSTOM_TRIGGER, label: '직접 입력' },
];

/** 사전 정의된 옵션 value 집합 — 어댑터에서 저장된 문자열을 복원할 때 커스텀 항목 판별에 사용 */
export const CR_QUESTION_PREDEFINED_VALUES = new Set(
  CR_QUESTION_OPTIONS.map((o) => o.value).filter((v) => v !== CUSTOMER_REQUESTS_CUSTOM_TRIGGER)
);
export const CR_PAID_PREDEFINED_VALUES = new Set(
  CR_PAID_OPTIONS.map((o) => o.value).filter((v) => v !== CUSTOMER_REQUESTS_CUSTOM_TRIGGER)
);

/**
 * 추가 요청사항 + 비교견적 FlowDocument — 두 step, 각 step 은 multi-select extensible 1개.
 * 어댑터: `CustomerRequestsStepAdapter`.
 */
export function buildCustomerRequestsFlow(): FlowDocument {
  return {
    schemaVersion: 1,
    id: 'progress-customer-requests',
    initial: CR_STEP_QUESTIONS,
    meta: { appBarTitle: '추가 요청사항' },
    steps: {
      [CR_STEP_QUESTIONS]: {
        kind: 'questions',
        questions: [
          {
            id: CUSTOMER_REQUESTS_QUESTION_ID,
            layout: 'L1',
            title: '추가로 요청하실 내용이 있으신가요?',
            ctaLabel: '다음',
            input: {
              kind: 'multi-select',
              required: false,
              options: CR_QUESTION_OPTIONS,
              extensible: {
                triggerValue: CUSTOMER_REQUESTS_CUSTOM_TRIGGER,
                sheetTitle: '그 외 요청사항을 알려주세요',
                placeholder: '직접 입력',
                confirmLabel: '추가',
                maxLength: 100,
              },
            },
          },
        ],
        next: { kind: 'goto', target: CR_STEP_PAID },
      },
      [CR_STEP_PAID]: {
        kind: 'questions',
        questions: [
          {
            id: CUSTOMER_REQUESTS_PAID_QUESTION_ID,
            layout: 'L1',
            title: '비교견적이 필요하신가요?',
            ctaLabel: '완료',
            input: {
              kind: 'multi-select',
              required: false,
              options: CR_PAID_OPTIONS,
              extensible: {
                triggerValue: CUSTOMER_REQUESTS_CUSTOM_TRIGGER,
                sheetTitle: '비교견적 직접 입력',
                placeholder: '직접 입력',
                confirmLabel: '추가',
                maxLength: 100,
              },
            },
          },
        ],
        next: { kind: 'terminal', target: CR_TERMINAL },
      },
    },
    terminals: { [CR_TERMINAL]: { kind: 'placeholder-auto' } },
  };
}
